import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { PageWrap, PageHead, Card } from './customerUi';
import { isStudentPrincipal } from '../../services/principalStudents';

/* More — the secondary destinations in one calm list. Account and Family are
   SEPARATE rows (Anthony's decision; the mockup combined them). Materials keeps
   linking to the existing public /requests form — no in-portal materials flow
   exists yet, so re-homing it is out of scope. Log out reuses the same
   AuthContext logout the chrome menu uses. */

const ROWS = [
	/* Lesson Recaps (design pass 2, change 12). First, because it is the one row
	   here a family opens for the child rather than for the account. Shared with
	   a student principal by default; the page narrows its own query. */
	{
		icon: 'fa-comment-lines',
		title: 'Lesson Recaps',
		sub: 'What your teacher shared after each lesson',
		to: '/portal/customer/recaps',
	},
	{
		icon: 'fa-credit-card',
		title: 'Payments',
		sub: 'Payment history & billing',
		to: '/portal/customer/payments',
		guardianOnly: true,
	},
	{
		icon: 'fa-user',
		title: 'Account',
		sub: 'Email & sign-in',
		to: '/portal/customer/account',
	},
	{
		icon: 'fa-people-roof',
		title: 'Family',
		sub: 'Your students & contacts',
		to: '/portal/customer/family',
		guardianOnly: true,
	},
	/* Materials request — HIDDEN, not deleted (Anthony, 2026-08-10 portal review:
	   "Comment out the Materials Request link until pricing and scope are
	   finalized… safely commented out rather than removed, preserving the code
	   for future activation").

	   Two reasons it had to come out of the parent's menu rather than stay and
	   be fixed later. It pointed at `/requests`, which is a PUBLIC unauthenticated
	   page — so the one item in this list that left the portal was also the one
	   with no auth behind it. And pricing is undecided: custom materials are real
	   work the studio has historically not charged for, so the form currently
	   invites a request the studio has no settled answer to.

	   Restoring this is deleting this comment block. Both open questions live on
	   the portal card "Materials request: gate behind auth + review pricing". */
	// {
	// 	icon: 'fa-book-open',
	// 	title: 'Materials request',
	// 	sub: 'Books & practice tracks',
	// 	to: '/requests',
	// },
	{
		icon: 'fa-bullhorn',
		title: 'Announcements',
		sub: 'News from the studio',
		to: '/portal/customer/announcements',
	},
	{
		icon: 'fa-calendar-days',
		title: 'Season calendar',
		sub: 'Recitals, rehearsals & key dates',
		to: '/portal/customer/calendar',
	},
];

const rowBase = {
	display: 'flex',
	alignItems: 'center',
	gap: 13,
	padding: '13px 15px',
	textDecoration: 'none',
};

const tile = (bg, color) => ({
	width: 38,
	height: 38,
	borderRadius: 11,
	background: bg,
	color,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: '0 0 auto',
});

const More = () => {
	const { currentUser, userProfile, logout } = useAuth();
	const navigate = useNavigate();

	/* P2b — a student principal is denied Payments and Family at the route, so
	   offering them here would be a link to an Access Denied page. Filtering the
	   menu is the COURTESY half; the nested route guard in AnimatedRoutes is the
	   enforcement half, and neither substitutes for the other. Rows are filtered
	   rather than a separate student list being maintained, so a new row is
	   shared by default and has to opt OUT of the student's menu deliberately. */
	const rows = ROWS.filter(
		(r) => !r.guardianOnly || !isStudentPrincipal(userProfile)
	);

	const handleLogout = async () => {
		try {
			await logout();
			navigate('/portal/login');
		} catch (error) {
			console.error('Logout error:', error);
		}
	};

	return (
		<PageWrap>
			<PageHead title='More' />

			<Card style={{ marginBottom: 14 }}>
				{rows.map((r, i) => (
					<Link
						key={r.title}
						to={r.to}
						style={{
							...rowBase,
							borderBottom:
								i < rows.length - 1
									? '1px solid var(--cyv-line, #f0eae1)'
									: 'none',
						}}>
						<span
							style={tile(
								'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 13%, var(--surface-card, #fff))',
								'var(--cyv-teal-600, #2f7d77)'
							)}>
							<i className={`fa-sharp fa-solid ${r.icon}`} style={{ fontSize: 14 }} aria-hidden='true' />
						</span>
						<span style={{ flex: 1, minWidth: 0 }}>
							<span
								style={{
									display: 'block',
									fontSize: '0.92rem',
									fontWeight: 'var(--fw-bold, 700)',
									color: 'var(--text-heading, #384971)',
								}}>
								{r.title}
							</span>
							<span
								style={{
									display: 'block',
									fontSize: '0.78rem',
									color: 'var(--text-muted, #7b8499)',
									marginTop: 2,
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}>
								{r.sub}
							</span>
						</span>
						<i
							className='fa-sharp fa-solid fa-chevron-right'
							style={{ fontSize: 11, color: 'var(--text-faint, #b8c0cc)' }}
							aria-hidden='true'
						/>
					</Link>
				))}
			</Card>

			<Card>
				<button
					type='button'
					onClick={handleLogout}
					style={{
						...rowBase,
						width: '100%',
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						fontFamily: 'inherit',
						textAlign: 'left',
					}}>
					<span
						style={tile(
							'color-mix(in srgb, var(--cyv-danger, #d9776a) 12%, var(--surface-card, #fff))',
							'var(--cyv-danger, #d9776a)'
						)}>
						<i className='fa-sharp fa-solid fa-arrow-right-from-bracket' style={{ fontSize: 14 }} aria-hidden='true' />
					</span>
					<span
						style={{
							fontSize: '0.92rem',
							fontWeight: 'var(--fw-bold, 700)',
							color: 'var(--cyv-danger, #d9776a)',
						}}>
						Log out
					</span>
				</button>
			</Card>

			<div
				style={{
					fontSize: '0.78rem',
					color: 'var(--text-muted, #9aa2b1)',
					textAlign: 'center',
					marginTop: 16,
				}}>
				Signed in as {currentUser?.email}
			</div>
		</PageWrap>
	);
};

export default More;
